import { withAnalyticsTransportDeadline } from "@/lib/analytics/analyticsTransportDeadline";
import type { WebAnalyticsEventEnvelope, WebAnalyticsEventStoreResult } from "@/lib/analytics/types";
import { logger } from "@/lib/logging/logger";

type SubmitBatch = (
  events: WebAnalyticsEventEnvelope[],
  signal?: AbortSignal,
) => Promise<WebAnalyticsEventStoreResult[]>;

export const WEB_ANALYTICS_BATCH_SIZE = 50;
export const WEB_ANALYTICS_FLUSH_INTERVAL_MS = 3_000;
export const WEB_ANALYTICS_OUTBOX_LIMIT = 2_000;
// Fetch keepalive bodies share a browser-managed 64 KiB budget. Two 24 KiB event batches may
// overlap when navigation happens during a regular poll, leaving headroom for tRPC framing and
// the tiny consent/identifier observations.
export const WEB_ANALYTICS_KEEPALIVE_PAYLOAD_BUDGET_BYTES = 24 * 1_024;
const MAX_CONCURRENT_EVENT_BATCHES = 2;

const log = logger.child("MarketingEventOutbox");
const eventsById = new Map<string, WebAnalyticsEventEnvelope>();
const inFlightEventIds = new Set<string>();
const activeBatchPromises = new Set<Promise<number>>();
let activeBatchCount = 0;
let activeSubmitBatch: SubmitBatch | undefined;
let captureSuspensionCount = 0;
const utf8Encoder = typeof TextEncoder === "undefined" ? undefined : new TextEncoder();

/** Adds one typed event occurrence to the bounded document-memory FIFO. */
export function enqueueMarketingEvent(event: WebAnalyticsEventEnvelope): void {
  if (captureSuspensionCount > 0) return;
  if (eventsById.has(event.eventId)) return;

  if (eventsById.size >= WEB_ANALYTICS_OUTBOX_LIMIT) {
    let oldestEvictableEventId: string | undefined;
    for (const eventId of eventsById.keys()) {
      if (inFlightEventIds.has(eventId)) continue;
      oldestEvictableEventId = eventId;
      break;
    }
    if (!oldestEvictableEventId) {
      log.warn("Analytics queue capacity reached; dropped newest event", {
        attributes: { rejectedCount: 1 },
      });
      return;
    }
    eventsById.delete(oldestEvictableEventId);
    log.warn("Analytics queue capacity reached; dropped oldest event", {
      attributes: { evictedCount: 1 },
    });
  }
  eventsById.set(event.eventId, event);
}

function acknowledgedEventIds(results: unknown, submittedIds: ReadonlySet<string>): ReadonlySet<string> {
  if (!Array.isArray(results)) return new Set();
  const acknowledged = new Set<string>();
  for (const result of results) {
    if (typeof result !== "object" || result === null) continue;
    const { eventId, status } = result as { eventId?: unknown; status?: unknown };
    if (
      typeof eventId === "string" &&
      submittedIds.has(eventId) &&
      (status === "stored" || status === "duplicate" || status === "dropped")
    ) {
      acknowledged.add(eventId);
    }
  }
  return acknowledged;
}

function utf8ByteLength(value: string): number {
  // Three bytes per UTF-16 code unit is a conservative fallback for old browsers without
  // TextEncoder (surrogate pairs are over-counted, which is safe for the keepalive budget).
  return utf8Encoder?.encode(value).byteLength ?? value.length * 3;
}

function getNextBatch(): WebAnalyticsEventEnvelope[] {
  const batch: WebAnalyticsEventEnvelope[] = [];
  let payloadBytes = utf8ByteLength('{"events":[]}');

  for (const event of eventsById.values()) {
    if (inFlightEventIds.has(event.eventId)) continue;
    const eventBytes = utf8ByteLength(JSON.stringify(event)) + (batch.length > 0 ? 1 : 0);
    if (batch.length > 0 && payloadBytes + eventBytes > WEB_ANALYTICS_KEEPALIVE_PAYLOAD_BUDGET_BYTES) break;

    batch.push(event);
    payloadBytes += eventBytes;
    if (batch.length >= WEB_ANALYTICS_BATCH_SIZE) break;
  }

  return batch;
}

async function submitNextBatch(submitBatch: SubmitBatch): Promise<number> {
  const batch = getNextBatch();
  if (batch.length === 0) return 0;

  activeBatchCount += 1;
  for (const { eventId } of batch) inFlightEventIds.add(eventId);
  try {
    const submittedIds = new Set(batch.map(({ eventId }) => eventId));
    const results: unknown = await withAnalyticsTransportDeadline((signal) => submitBatch(batch, signal));
    for (const eventId of acknowledgedEventIds(results, submittedIds)) eventsById.delete(eventId);
  } catch (error) {
    log.warn("Failed to store browser analytics batch; keeping events for next poll", {
      error,
      attributes: { eventCount: batch.length },
    });
  } finally {
    for (const { eventId } of batch) inFlightEventIds.delete(eventId);
    activeBatchCount -= 1;
  }

  return batch.length;
}

function startNextBatch(submitBatch: SubmitBatch): Promise<number> {
  const submission = submitNextBatch(submitBatch);
  activeBatchPromises.add(submission);
  void submission.then(
    () => activeBatchPromises.delete(submission),
    () => activeBatchPromises.delete(submission),
  );
  return submission;
}

/** Submits at most one non-overlapping poll batch. */
export async function flushMarketingEventOutboxOnce(submitBatch: SubmitBatch): Promise<number> {
  if (captureSuspensionCount > 0 || activeBatchCount > 0) return 0;
  return startNextBatch(submitBatch);
}

/** Starts a best-effort immediate drain through the active keepalive-capable transport. */
export function flushMarketingEventOutboxNow(): void {
  if (captureSuspensionCount > 0 || !activeSubmitBatch || activeBatchCount >= MAX_CONCURRENT_EVENT_BATCHES) return;
  // In-flight ids are excluded, so navigation can start one additional bounded keepalive request
  // containing only events that are not already represented by the regular poll.
  void startNextBatch(activeSubmitBatch);
}

/**
 * Quarantines the document outbox before changing the authenticated acting identity.
 *
 * New captures and poll attempts are paused first. One final bounded batch is then started while
 * the server still has the old authoritative identity state, and every already-started request is
 * allowed to settle or hit its transport deadline. Anything still only in memory is discarded
 * before the caller may change that server state. Invoke the returned callback only when the
 * identity mutation fails and the document remains on the old identity.
 */
export async function prepareMarketingEventOutboxForIdentityBoundary(): Promise<() => void> {
  captureSuspensionCount += 1;

  if (activeSubmitBatch && activeBatchCount < MAX_CONCURRENT_EVENT_BATCHES) {
    void startNextBatch(activeSubmitBatch);
  }

  await Promise.allSettled([...activeBatchPromises]);

  const discardedCount = eventsById.size;
  eventsById.clear();
  inFlightEventIds.clear();
  if (discardedCount > 0) {
    log.info("Discarded browser analytics at identity boundary", {
      attributes: { discardedCount },
    });
  }

  let resumed = false;
  return () => {
    if (resumed) return;
    resumed = true;
    captureSuspensionCount = Math.max(0, captureSuspensionCount - 1);
  };
}

/** Starts one simple non-overlapping poll loop at a three-second cadence. */
export function startMarketingEventOutboxPoller(submitBatch: SubmitBatch): () => void {
  let stopped = false;
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  activeSubmitBatch = submitBatch;

  const handlePageHide = (): void => {
    flushMarketingEventOutboxNow();
  };
  const handleVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") flushMarketingEventOutboxNow();
  };
  if (typeof window !== "undefined") window.addEventListener("pagehide", handlePageHide);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", handleVisibilityChange);

  const schedule = (delay: number): void => {
    if (stopped) return;
    timer = globalThis.setTimeout(() => {
      timer = undefined;
      void flush();
    }, delay);
  };

  const flush = async (): Promise<void> => {
    if (stopped) return;
    await flushMarketingEventOutboxOnce(submitBatch);
    schedule(WEB_ANALYTICS_FLUSH_INTERVAL_MS);
  };

  schedule(0);
  return () => {
    stopped = true;
    if (timer !== undefined) globalThis.clearTimeout(timer);
    if (typeof window !== "undefined") window.removeEventListener("pagehide", handlePageHide);
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", handleVisibilityChange);
    if (activeSubmitBatch === submitBatch) activeSubmitBatch = undefined;
  };
}

export function resetMarketingEventOutboxForTests(): void {
  eventsById.clear();
  inFlightEventIds.clear();
  activeBatchPromises.clear();
  activeBatchCount = 0;
  activeSubmitBatch = undefined;
  captureSuspensionCount = 0;
}
